import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { 
  FiPlay, FiMonitor, FiSmartphone, 
  FiArrowRight, FiArrowLeft, FiCheck, FiUploadCloud, 
  FiLayers, FiZap, FiVideo, FiCalendar, FiMail,
  FiFacebook, FiInstagram, FiLinkedin, FiYoutube,
  FiCopy, FiGlobe, FiX
} = FiIcons;

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [showMailOptions, setShowMailOptions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPcVideo, setShowPcVideo] = useState(false);
  const [pcVideoStarted, setPcVideoStarted] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!showPcVideo) {
      setPcVideoStarted(false);
    }
  }, [showPcVideo]);

  const totalSteps = 6;
  const stripeLink = "https://buy.stripe.com/28EeV75zjdlv2hS6Cf7Vm01";

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resetOnboarding = () => {
    setCurrentStep(0);
    setSelectedDevice(null);
    setUserEmail('');
    setShowMailOptions(false);
    setCopied(false);
    setShowPcVideo(false);
    setPcVideoStarted(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center space-y-6 md:space-y-8 pb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 md:space-y-8 w-full max-w-3xl px-4 flex flex-col items-center"
            >
              <h1 className="text-4xl md:text-7xl font-black tracking-tighter text-white uppercase leading-tight">
                Welcome <br />
                <span className="brand-gradient-text">Onboard</span>
              </h1>
              
              <div 
                className="relative w-full bg-[#0A1628] rounded-2xl md:rounded-3xl border border-[#1E2D4E] overflow-hidden shadow-2xl" 
                style={{ aspectRatio: '1/1' }}
              >
                <iframe
                  loading="lazy" 
                  title="Gumlet video player"
                  src="https://play.gumlet.io/embed/69be594961788532c9326258?background=false&autoplay=false&loop=false&disable_player_controls=true"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  referrerPolicy="origin"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
                ></iframe>
              </div>

              <p className="text-sm md:text-lg text-[#94A3B8] max-w-lg mx-auto leading-relaxed">
                Watch the briefing above to begin your Profitlense indicator configuration.
              </p>

              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={nextStep}
                className="mt-6 w-full sm:w-auto sm:min-w-[280px] py-4 md:py-5 px-8 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-xs md:text-sm shadow-xl shadow-[#7B2FBE]/20 hover:bg-[#8B5CF6] hover:-translate-y-1 transition-all flex items-center justify-center space-x-3"
              >
                <span>Start Journey</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6 md:space-y-8 max-w-2xl mx-auto px-4">
            <div className="text-center space-y-1 md:space-y-2">
              <span className="text-[#7B2FBE] font-black text-[10px] md:text-sm uppercase tracking-[0.3em]">Step 01</span>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-tight text-white">
                Choose your <br className="md:hidden" />device type
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <button 
                onClick={() => setSelectedDevice('phone')}
                className={`p-6 md:p-8 rounded-2xl md:rounded-3xl border-2 transition-all flex flex-row md:flex-col items-center space-x-4 md:space-x-0 md:space-y-4 group ${
                  selectedDevice === 'phone' 
                  ? 'bg-[#7B2FBE] border-[#7B2FBE] text-white shadow-xl shadow-[#7B2FBE]/20' 
                  : 'bg-[#0A1628] border-[#1E2D4E] text-[#94A3B8] hover:border-[#7B2FBE]/50'
                }`}
              >
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center transition-colors shrink-0 ${
                  selectedDevice === 'phone' ? 'bg-white/20' : 'bg-[#1E2D4E] group-hover:bg-[#7B2FBE]/20'
                }`}>
                  <SafeIcon icon={FiSmartphone} className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <div className="text-left md:text-center">
                  <span className="block font-black text-lg md:text-xl uppercase tracking-tighter">Mobile Phone</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest opacity-60">Handheld Setup</span>
                </div>
              </button>

              <button 
                onClick={() => {
                  setSelectedDevice('pc');
                  setShowPcVideo(true);
                }}
                className={`p-6 md:p-8 rounded-2xl md:rounded-3xl border-2 transition-all flex flex-row md:flex-col items-center space-x-4 md:space-x-0 md:space-y-4 group ${
                  selectedDevice === 'pc' 
                  ? 'bg-[#7B2FBE] border-[#7B2FBE] text-white shadow-xl shadow-[#7B2FBE]/20' 
                  : 'bg-[#0A1628] border-[#1E2D4E] text-[#94A3B8] hover:border-[#7B2FBE]/50'
                }`}
              >
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center transition-colors shrink-0 ${
                  selectedDevice === 'pc' ? 'bg-white/20' : 'bg-[#1E2D4E] group-hover:bg-[#7B2FBE]/20'
                }`}>
                  <SafeIcon icon={FiMonitor} className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <div className="text-left md:text-center">
                  <span className="block font-black text-lg md:text-xl uppercase tracking-tighter">PC / Laptop</span>
                  <span className="text-[9px] font-bold uppercase tracking-widest opacity-60">Desktop Setup</span>
                </div>
              </button>
            </div>
            {isMobile && selectedDevice && (
               <button onClick={nextStep} className="w-full py-5 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-xs shadow-lg mt-4">
                 CONTINUE
               </button>
            )}
          </div>
        );

      case 2:
        const commonRecipients = 'profitlense.support@gmail.com,support@profitlense.ai';
        const outlookRecipients = 'profitlense.support@gmail.com;support@profitlense.ai';
        const subject = encodeURIComponent("Please check my screenshot of setup");
        const bodyText = `Hello Profitlense Support,\n\nI have completed my indicator setup and would like to verify it.\n\nMy Registered Email: ${userEmail}\n\n[PLEASE ATTACH YOUR SETUP SCREENSHOTS TO THIS EMAIL]\n\nThank you.`;
        const body = encodeURIComponent(bodyText);

        const mailtoHref = `mailto:${commonRecipients}?subject=${subject}&body=${body}`;
        const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${commonRecipients}&su=${subject}&body=${body}`;
        const outlookHref = `https://outlook.live.com/mail/0/deeplink/compose?to=${outlookRecipients}&subject=${subject}&body=${body}`;

        const handleCopy = async () => {
          const copyText = `To: ${commonRecipients}\nSubject: Please check my screenshot of setup\n\n${bodyText}`;
          try {
            await navigator.clipboard.writeText(copyText);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
          } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = copyText;
            document.body.appendChild(textArea);
            textArea.select();
            try {
              document.execCommand('copy');
              setCopied(true);
              setTimeout(() => setCopied(false), 3000);
            } catch (e) {
              console.error("Copy failed", e);
            }
            document.body.removeChild(textArea);
          }
        };

        return (
          <div className="space-y-6 md:space-y-8 max-w-2xl mx-auto px-4">
            <div className="text-center space-y-2">
              <span className="text-[#7B2FBE] font-black text-[10px] md:text-sm uppercase tracking-[0.3em]">Step 02</span>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">
                Submit for <br className="md:hidden" />Verification
              </h2>
               <p className="text-[#94A3B8] text-[10px] md:text-xs uppercase tracking-widest font-bold">
                Our team will verify your setup manually
              </p>
            </div>

            {showMailOptions ? (
               <motion.div 
                 initial={{ opacity: 0, scale: 0.95 }} 
                 animate={{ opacity: 1, scale: 1 }} 
                 className="bg-[#0A1628] border border-[#1E2D4E] rounded-2xl md:rounded-3xl p-6 md:p-8 space-y-6 shadow-2xl"
               >
                 <div className="text-center space-y-2 mb-6">
                   <div className="w-12 h-12 bg-[#7B2FBE]/20 border border-[#7B2FBE]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                     <SafeIcon icon={FiMail} className="text-[#7B2FBE] w-5 h-5" />
                   </div>
                   <h3 className="text-lg md:text-xl font-black uppercase text-white tracking-tight">Choose Email Method</h3>
                   <p className="text-[10px] md:text-xs text-[#94A3B8] font-medium max-w-sm mx-auto">
                     Select your preferred email provider to send the screenshots.
                   </p>
                 </div>

                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                   <a 
                     href={mailtoHref} 
                     target="_top" 
                     className="flex items-center justify-center space-x-3 bg-[#1E2D4E] hover:bg-[#7B2FBE] p-4 rounded-xl transition-all font-bold text-[10px] md:text-xs uppercase tracking-widest text-white border border-[#1E2D4E] hover:border-[#7B2FBE] group"
                   >
                     <SafeIcon icon={FiSmartphone} className="w-4 h-4 opacity-70 group-hover:opacity-100" />
                     <span>Default App</span>
                   </a>
                   <a 
                     href={gmailHref} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="flex items-center justify-center space-x-3 bg-[#1E2D4E] hover:bg-[#7B2FBE] p-4 rounded-xl transition-all font-bold text-[10px] md:text-xs uppercase tracking-widest text-white border border-[#1E2D4E] hover:border-[#7B2FBE] group"
                   >
                     <SafeIcon icon={FiGlobe} className="w-4 h-4 opacity-70 group-hover:opacity-100" />
                     <span>Gmail (Web)</span>
                   </a>
                   <a 
                     href={outlookHref} 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="flex items-center justify-center space-x-3 bg-[#1E2D4E] hover:bg-[#7B2FBE] p-4 rounded-xl transition-all font-bold text-[10px] md:text-xs uppercase tracking-widest text-white border border-[#1E2D4E] hover:border-[#7B2FBE] group"
                   >
                     <SafeIcon icon={FiGlobe} className="w-4 h-4 opacity-70 group-hover:opacity-100" />
                     <span>Outlook (Web)</span>
                   </a>
                   <button 
                     onClick={handleCopy} 
                     className={`flex items-center justify-center space-x-3 p-4 rounded-xl transition-all font-bold text-[10px] md:text-xs uppercase tracking-widest border ${
                       copied 
                       ? 'bg-green-500/10 border-green-500/30 text-green-500' 
                       : 'bg-[#0D1B3E] border-[#1E2D4E] text-[#94A3B8] hover:text-white hover:border-[#7B2FBE]'
                     }`}
                   >
                     <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
                     <span>{copied ? 'Copied Details' : 'Copy Details'}</span>
                   </button>
                 </div>

                 <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center mt-2">
                   <p className="text-[9px] md:text-[10px] text-red-400 font-bold uppercase tracking-widest">
                     ⚠️ Remember to attach your setup screenshots manually!
                   </p>
                 </div>

                 {!isMobile && (
                   <button 
                     onClick={nextStep} 
                     className="w-full py-4 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs shadow-lg shadow-[#7B2FBE]/20 hover:bg-[#8B5CF6] transition-all mt-4 flex items-center justify-center space-x-2"
                   >
                     <span>I've Sent The Email</span>
                     <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                   </button>
                 )}
               </motion.div>
            ) : (
              <div className="bg-[#0A1628] border border-[#1E2D4E] rounded-2xl md:rounded-3xl p-6 md:p-10 space-y-6 shadow-2xl">
                <div className="space-y-4">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">
                    Enter your registered email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="text-[#7B2FBE] w-5 h-5" />
                    </div>
                    <input 
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-[#0D1B3E] border border-[#1E2D4E] rounded-xl py-4 pl-12 pr-4 text-white focus:outline-none focus:border-[#7B2FBE] transition-all font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <button 
                    onClick={() => setShowMailOptions(true)}
                    disabled={!userEmail.includes('@')}
                    className={`w-full py-5 rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs transition-all flex items-center justify-center space-x-3 ${
                      userEmail.includes('@') 
                      ? 'bg-[#7B2FBE] text-white shadow-lg shadow-[#7B2FBE]/20 hover:bg-[#8B5CF6]' 
                      : 'bg-[#1E2D4E] text-[#94A3B8] cursor-not-allowed'
                    }`}
                  >
                    <span>Proceed To Send</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                  <p className="text-[9px] text-center text-[#94A3B8] font-medium leading-relaxed">
                    You will be able to choose your preferred email provider <br className="hidden md:block"/>
                    (Gmail, Outlook, or Default App) on the next screen.
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 md:space-y-8 max-w-3xl mx-auto px-4">
            <div className="text-center space-y-2 md:space-y-4">
              <span className="text-[#7B2FBE] font-black text-[10px] md:text-sm uppercase tracking-[0.3em]">Step 03</span>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-tight text-white">
                Learning center
              </h2>
              <p className="text-[#94A3B8] text-[10px] md:text-sm uppercase tracking-widest font-bold max-w-sm mx-auto">
                Review these essential masterclass resources
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {[
                { icon: FiLayers, title: 'Guide', label: 'Docs', url: 'https://guide.profitlense.ai/' },
                { icon: FiZap, title: 'Interactive', label: 'Simulator', url: 'https://profitlense.ai/our-interactive-guides/' },
                { icon: FiVideo, title: 'Video', label: 'Playlist', url: 'https://youtube.com/playlist?list=PLCxNlWZlENzBRN2wg7BRQtohfBxT4iRBg' }
              ].map((res, i) => (
                <a key={i} href={res.url} target="_blank" rel="noopener noreferrer" className="p-5 md:p-6 bg-[#0A1628] border border-[#1E2D4E] rounded-xl md:rounded-2xl flex flex-row md:flex-col items-center md:text-center space-x-4 md:space-x-0 md:space-y-4 hover:border-[#7B2FBE] transition-all group">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#1E2D4E] flex items-center justify-center text-[#7B2FBE] group-hover:bg-[#7B2FBE] group-hover:text-white transition-all shrink-0">
                    <SafeIcon icon={res.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-black uppercase tracking-tighter text-white text-sm md:text-base">{res.title}</h3>
                    <p className="text-[8px] md:text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest mt-1">{res.label}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="flex justify-center mt-8 md:mt-10">
               <button 
                 onClick={nextStep} 
                 className="w-full sm:w-auto sm:min-w-[320px] py-4 md:py-5 px-8 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs shadow-xl shadow-[#7B2FBE]/20 hover:bg-[#8B5CF6] hover:-translate-y-1 transition-all flex items-center justify-center space-x-3"
               >
                 <span>Next Step</span>
                 <SafeIcon icon={FiArrowRight} className="w-4 h-4 md:w-5 md:h-5" />
               </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 md:space-y-8 max-w-2xl mx-auto px-4">
            <div className="text-center space-y-2 md:space-y-4">
              <span className="text-[#7B2FBE] font-black text-[10px] md:text-sm uppercase tracking-[0.3em]">Step 04</span>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-tight text-white">
                Personal Training
              </h2>
              <p className="text-[#94A3B8] text-xs md:text-sm font-medium leading-relaxed">
                Book a 1-1 session with our specialist trainer to optimize your strategy.
              </p>
            </div>

            <div className="bg-[#0A1628] border border-[#1E2D4E] rounded-2xl md:rounded-3xl p-6 md:p-10 text-center space-y-6 shadow-2xl">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-[#7B2FBE]/10 rounded-full flex items-center justify-center mx-auto border border-[#7B2FBE]/20">
                <SafeIcon icon={FiCalendar} className="w-8 h-8 md:w-10 md:h-10 text-[#7B2FBE]" />
              </div>
              <button className="w-full bg-[#7B2FBE] text-white py-4 md:py-5 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[10px] md:text-xs hover:bg-[#8B5CF6] transition-all shadow-xl shadow-[#7B2FBE]/20">
                Book Training Slot
              </button>
              <p className="text-[8px] md:text-[10px] text-[#94A3B8] uppercase tracking-widest font-bold">
                highly recommended for beginners
              </p>
            </div>
            {isMobile && (
               <button onClick={nextStep} className="w-full py-5 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-xs">
                 SKIP TO FINAL STEP
               </button>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-8 md:space-y-12 max-w-3xl mx-auto text-center px-4 pb-12">
             <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="space-y-4 md:space-y-6">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-green-500/30">
                <SafeIcon icon={FiCheck} className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <div className="space-y-3">
                <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight">Congratulations</h2>
                <p className="text-[#7B2FBE] text-sm md:text-lg font-black uppercase tracking-widest">Your trading journey begins</p>
                <p className="text-[#94A3B8] text-xs md:text-sm max-w-md mx-auto font-medium leading-relaxed pt-2">
                  You are now fully set to start working with Profitlense.ai. Feel free to reach out to us anytime if you encounter any issues.
                </p>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-[#0A1628] border border-[#1E2D4E] p-6 md:p-8 rounded-2xl md:rounded-3xl space-y-4 text-left">
                <p className="text-[8px] md:text-sm font-bold text-[#94A3B8] uppercase tracking-widest">Premium Service</p>
                <h3 className="text-lg md:text-xl font-black uppercase text-white leading-tight">Unlock full potential with a subscription</h3>
                <a href={stripeLink} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#7B2FBE] text-white py-4 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-[#8B5CF6] transition-all text-center shadow-lg">
                  Subscribe Now
                </a>
              </div>

              <div className="bg-[#0A1628] border border-[#1E2D4E] p-6 md:p-8 rounded-2xl md:rounded-3xl space-y-4 text-left">
                <p className="text-[8px] md:text-sm font-bold text-[#94A3B8] uppercase tracking-widest">Expert Help</p>
                <h3 className="text-lg md:text-xl font-black uppercase text-white leading-tight">Technical support available 24/7</h3>
                <button className="w-full border border-[#7B2FBE] text-[#7B2FBE] py-3.5 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-[#7B2FBE] hover:text-white transition-all">
                  Contact Support
                </button>
              </div>
            </div>

            <div className="space-y-6 pt-4 md:pt-8">
              <p className="text-[9px] font-black uppercase tracking-[0.4em] text-[#94A3B8]">Join the global community</p>
              <div className="flex justify-center space-x-4 md:space-x-6">
                {[
                  { icon: FiFacebook, url: 'https://www.facebook.com/profitlenseai' },
                  { icon: FiInstagram, url: 'https://www.instagram.com/profitlense_ai/' },
                  { icon: FiLinkedin, url: 'https://www.linkedin.com/company/profitlense-ai/' },
                  { icon: FiYoutube, url: 'https://www.youtube.com/@ProfitLenseai' }
                ].map((social, i) => (
                  <a key={i} href={social.url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#1E2D4E] flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-[#7B2FBE] hover:border-[#7B2FBE] transition-all">
                    <SafeIcon icon={social.icon} className="w-4 h-4 md:w-5 md:h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B3E] text-white selection:bg-[#7B2FBE] selection:text-white flex flex-col font-sans overflow-x-hidden relative">
      <header className="absolute top-0 left-0 right-0 z-50 py-4 px-4 md:px-6 flex justify-between items-center bg-transparent">
        <div className="flex items-center space-x-4 md:space-x-8">
          <div className="flex items-center cursor-pointer group" onClick={resetOnboarding}>
            <img 
              src="https://i.postimg.cc/0Qj6vTd6/Profit-Lense-Ai-removebg-preview.png" 
              alt="Profitlense" 
              className="h-16 md:h-32 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-3 md:space-x-6">
           <a href={stripeLink} target="_blank" rel="noopener noreferrer" className="hidden sm:block px-6 py-2.5 bg-[#7B2FBE] text-white rounded-full font-black uppercase tracking-widest text-[10px] hover:bg-[#8B5CF6] transition-all">
             Subscribe
           </a>
           <div className="flex items-center space-x-2 md:space-x-4">
              <div className="w-16 md:w-24 h-1 bg-[#1E2D4E] rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#7B2FBE]" initial={{ width: 0 }} animate={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }} />
              </div>
              <span className="text-[8px] md:text-[10px] font-black text-[#94A3B8] uppercase tracking-widest whitespace-nowrap">
                {currentStep + 1} / {totalSteps}
              </span>
           </div>
        </div>
      </header>

      <div className="flex flex-1 pt-24 md:pt-40 pb-20 md:pb-12">
        <main className="flex-1 px-4 md:px-12 flex items-center justify-center relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, scale: 0.98, x: -10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.98, x: 10 }}
              transition={{ duration: 0.4, ease: "circOut" }}
              className="w-full max-w-4xl"
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {!isMobile && currentStep > 0 && currentStep < totalSteps - 1 && (
            <button onClick={prevStep} className="fixed bottom-10 left-10 text-[#94A3B8] hover:text-[#8B5CF6] flex items-center space-x-2 font-bold text-xs uppercase transition-colors">
              <SafeIcon icon={FiArrowLeft} />
              <span>Back</span>
            </button>
          )}
        </main>

        {!isMobile && (
          <aside className="w-20 md:w-32 flex flex-col items-center justify-center border-l border-[#1E2D4E]/50 sticky top-0 h-screen bg-gradient-to-b from-transparent via-[#7B2FBE]/5 to-transparent">
            {currentStep < totalSteps - 1 ? (
              <button 
                onClick={nextStep} 
                className={`group flex flex-col items-center space-y-4 ${currentStep === 2 && !showMailOptions ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all ${
                  currentStep === 2 && !showMailOptions 
                  ? 'bg-[#1E2D4E]' 
                  : 'bg-[#7B2FBE] shadow-[#7B2FBE]/20 group-hover:bg-[#8B5CF6] group-hover:scale-105'
                }`}>
                  <SafeIcon icon={FiArrowRight} className={`w-8 h-8 ${currentStep === 2 && !showMailOptions ? 'text-[#94A3B8]' : 'text-white'}`} />
                </div>
                <span className={`text-[10px] font-black uppercase tracking-[0.3em] transition-colors text-center px-2 ${
                  currentStep === 2 && !showMailOptions ? 'text-[#94A3B8]' : 'text-[#7B2FBE] group-hover:text-[#8B5CF6]'
                }`}>
                  {currentStep === 0 ? 'START' : 'NEXT'}
                </span>
              </button>
            ) : (
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-[#7B2FBE] rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-8 h-8 text-white" />
                </div>
              </div>
            )}
          </aside>
        )}
      </div>

      {isMobile && currentStep < totalSteps - 1 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-[#0A1628]/95 backdrop-blur-md border-t border-[#1E2D4E] flex items-center justify-between">
          <button 
            onClick={prevStep} 
            disabled={currentStep === 0}
            className={`p-4 rounded-xl transition-all ${currentStep === 0 ? 'opacity-20' : 'bg-[#1E2D4E] text-white'}`}
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <button 
            onClick={nextStep}
            disabled={currentStep === 2 && !showMailOptions}
            className={`flex-1 ml-4 py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg flex items-center justify-center space-x-2 ${
              currentStep === 2 && !showMailOptions
              ? 'bg-[#1E2D4E] text-[#94A3B8] opacity-50 cursor-not-allowed'
              : 'bg-[#7B2FBE] text-white shadow-[#7B2FBE]/20'
            }`}
          >
            <span>{currentStep === 0 ? 'START JOURNEY' : (currentStep === 2 ? "I'VE SENT THE EMAIL" : 'CONTINUE')}</span>
            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
          </button>
        </div>
      )}

      <footer className="p-4 md:p-6 border-t border-[#1E2D4E]/50 flex flex-col md:flex-row space-y-4 md:space-y-0 justify-between items-center text-[8px] md:text-[10px] font-bold text-[#94A3B8] bg-[#0A1628]/50">
        <p className="uppercase tracking-widest text-center md:text-left">© 2026 PROFITLENSE AI • ALL RIGHTS RESERVED</p>
        <div className="flex space-x-4 md:space-x-6 uppercase tracking-[0.2em]">
          <a href="https://profitlense.ai/policy/" target="_blank" rel="noopener noreferrer" className="hover:text-[#7B2FBE] transition-colors">Privacy</a>
          <a href="https://profitlense.ai/terms/" target="_blank" rel="noopener noreferrer" className="hover:text-[#7B2FBE] transition-colors">Terms</a>
        </div>
      </footer>

      {/* PC Video Modal */}
      <AnimatePresence>
        {showPcVideo && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0A1628]/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-[#0D1B3E] border border-[#1E2D4E] p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-2xl w-full max-w-4xl relative"
            >
              <button 
                onClick={() => setShowPcVideo(false)}
                className="absolute -top-12 right-0 md:-right-4 md:-top-4 w-10 h-10 bg-[#1E2D4E] hover:bg-[#7B2FBE] text-white rounded-full flex items-center justify-center transition-colors border border-[#1E2D4E] hover:border-[#7B2FBE] z-10 shadow-lg"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>

              <div className="text-center mb-4 md:mb-6">
                <h3 className="text-xl md:text-2xl font-black uppercase text-white tracking-tight">PC / Laptop Guide</h3>
                <p className="text-xs md:text-sm text-[#94A3B8] mt-1 font-medium">Watch this quick video to understand the desktop setup process.</p>
              </div>

              <div className="relative w-full rounded-xl overflow-hidden bg-black border border-[#1E2D4E] shadow-xl" style={{ aspectRatio: '16/9' }}>
                {!pcVideoStarted && (
                  <div 
                    className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer group bg-black/10 hover:bg-black/30 transition-colors"
                    onClick={() => setPcVideoStarted(true)}
                  >
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-[#7B2FBE] rounded-full flex items-center justify-center group-hover:bg-[#8B5CF6] group-hover:scale-110 transition-all shadow-[0_0_30px_rgba(123,47,190,0.6)]">
                      <SafeIcon icon={FiPlay} className="w-8 h-8 md:w-10 md:h-10 text-white translate-x-1" />
                    </div>
                  </div>
                )}
                <iframe
                  loading="lazy" 
                  title="Gumlet video player"
                  src={`https://play.gumlet.io/embed/69c3b823123b739cbbaeb7d8?background=false&autoplay=${pcVideoStarted ? 'true' : 'false'}&loop=false&disable_player_controls=false`}
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  referrerPolicy="origin"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
                ></iframe>
              </div>

              <div className="mt-6 flex justify-center">
                <button 
                  onClick={() => {
                    setShowPcVideo(false);
                    nextStep();
                  }}
                  className="py-3 px-8 bg-[#7B2FBE] text-white rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs shadow-lg shadow-[#7B2FBE]/20 hover:bg-[#8B5CF6] transition-all"
                >
                  Setup done click here
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;